import { useEffect, useState, useRef } from "react";
import { useLocation } from "react-router-dom";

import { Box } from "ui/box";
import { Stack } from "ui/stack";
import { SubmitFormProps } from "components/modals/version-edit-modal/version-edit-modal";
import toast from "components/partials/toast/toast";
import { DesignPreview } from "containers/admin/clients/touchpoint/components/design-preview/design-preview";
import EmptyDesignPreview from "containers/admin/clients/touchpoint/components/empty-design-preview/empty-design-preview";

import {
  builderPreviewMenuOptions,
  BuilderPreviewPaneHeader,
  BuilderPreviewViews,
} from "containers/admin/clients/touchpoint/components/builder/builder-preview-pane-header/builder-preview-pane-header";
import {
  EmailHeaders,
  initialEmailHeaders,
} from "containers/admin/clients/touchpoint/components/email-builder/email-header-preview/email-header-preview";
import { LoadingSpinner } from "components/partials/loading-screen/loading-screen";
import { BuilderHtmlPreview } from "containers/admin/clients/touchpoint/components/builder/builder-html-preview/builder-html-preview";
import { NoVersionFound } from "containers/admin/clients/touchpoint/components/no-version-found/no-version-found";
import { EmailWriterPreviewLoadingState } from "containers/admin/clients/touchpoint/tab-components/preview-tab/components/email-writer-preview-loading-state/email-writer-preview-loading-state";
import { useIsEmailWriterGenerating } from "containers/admin/clients/touchpoint/hooks/use-is-email-writer-generating";

import { useFeatureFlag } from "contexts/feature-flag-context";

import useEmailTypes from "containers/admin/clients/client/components/email-types/use-email-types";
import { useCurrentCampaign } from "hooks/api/use-campaigns";
import { useCurrentClient } from "hooks/api/use-clients";
import { useCurrentUser } from "hooks/api/use-current-user";
import { useCurrentTouchpoint } from "hooks/api/use-current-touchpoint";
import {
  useCreateTouchpointVersion,
  useEditTouchpointVersion,
  useInvalidateTouchpointVersions,
  useTouchpointVersion,
  useTouchpointVersions,
  type TouchpointVersionAttributes,
} from "hooks/api/use-touchpoint-versions";
import type { EmailAttributes } from "hooks/api/use-touchpoint-versions";
import { getErrorMessage, addAnnotationIdToElements } from "utilities";
import { getSenderOnEmailTypeChange } from "containers/admin/clients/touchpoint/components/email-builder/email-builder-content-settings-tab/email-builder-content-settings-tab.helper";

import { TouchpointType } from "types/touchpoint";
import { removeUserIdFromString } from "utilities/touchpoint";
import { usesLandingPageBuilder } from "types/typeguards";
import { IEmailType } from "containers/admin/clients/client/components/email-types/email-types.types";
import { PROGRAM_TEMPLATES_CAMPAIGN_NAME } from "utilities/constants";
import AiContentFeedback from "containers/admin/clients/touchpoint/tab-components/preview-tab/components/ai-content-feedback/ai-content-feedback";
import { hasPermission } from "utilities/user";
import { TouchpointStatuses } from "types";
import { Permission } from "types/auth";

interface TouchpointPreviewTabContentProps {
  selectedVersion?: TouchpointVersionAttributes;
  setSelectedVersion: (version: TouchpointVersionAttributes) => void;
  isVersionHistoryLoading?: boolean;
}

type BeeModuleWithDescriptor = {
  descriptor?: {
    id?: unknown;
  };
};

type BeeColumnWithModules = {
  modules?: BeeModuleWithDescriptor[];
};

type BeeRowWithColumns = {
  columns?: BeeColumnWithModules[];
};

type BeeJsonOutput = {
  page?: {
    rows?: BeeRowWithColumns[];
  };
};

export const TouchpointPreviewTabContent = ({
  selectedVersion,
  setSelectedVersion,
  isVersionHistoryLoading,
}: TouchpointPreviewTabContentProps) => {
  const location = useLocation();
  const currentClient = useCurrentClient();
  const currentUser = useCurrentUser();
  const currentCampaign = useCurrentCampaign();
  const currentTouchpoint = useCurrentTouchpoint();
  const emailTypeOptions = useEmailTypes();
  const isEmailWriterGenerating = useIsEmailWriterGenerating();
  const isProgramBriefsEnabled = useFeatureFlag("program-briefs");
  const hasClientPortalAccess = hasPermission(currentUser, Permission.PERM_CLIENT_USER);
  const isTemplate = currentCampaign.name === PROGRAM_TEMPLATES_CAMPAIGN_NAME;
  const aiContentFeedbackHiddenStatuses = [
    TouchpointStatuses.ARCHIVED_DRAFT,
    TouchpointStatuses.CLIENT_REVIEW,
    TouchpointStatuses.APPROVED,
    TouchpointStatuses.PUBLISHED,
  ];

  const versionScope = {
    clientId: currentClient.id,
    campaignId: currentCampaign.id,
    touchpointId: currentTouchpoint.id,
  };
  const { refetch: refetchTouchpointVersions } = useTouchpointVersions(versionScope);
  const invalidateTouchpointVersions = useInvalidateTouchpointVersions(versionScope);
  const editVersionMutation = useEditTouchpointVersion(versionScope);
  const createVersionMutation = useCreateTouchpointVersion(versionScope);
  const { desktopView, mobileView } = builderPreviewMenuOptions;
  const [currentView, setCurrentView] = useState<BuilderPreviewViews>(mobileView);
  const [touchpointPreviewHTML, setTouchpointPreviewHTML] = useState<string>();
  const [touchpointFinalHTML, setTouchpointFinalHTML] = useState<string>();
  const [previewError, setPreviewError] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isCommentMode, setIsCommentMode] = useState<boolean>(false);
  const [isFullContentMode, setIsFullContentMode] = useState<boolean>(false);
  const [touchpointJSON, setTouchpointJSON] = useState<string>("");
  const [emailHeaders, setEmailHeaders] = useState<EmailHeaders>(initialEmailHeaders);

  const isDesktopPreview = currentView === desktopView;
  const isMobilePreview = currentView === mobileView;
  const isDesignPreview = isDesktopPreview || isMobilePreview;
  const emailOptionsDependency =
    currentTouchpoint.type === TouchpointType.EMAIL ? emailTypeOptions : undefined;
  const annotationBackfillVersionIdRef = useRef<string | undefined>(undefined);

  function setTouchpointValues(touchpointData: TouchpointVersionAttributes) {
    if (touchpointData.finalHtml) {
      const touchpointDisplayHTML = removeUserIdFromString(touchpointData.finalHtml);
      setTouchpointPreviewHTML(touchpointDisplayHTML);
      setTouchpointFinalHTML(touchpointData.finalHtml);
    }

    setTouchpointFinalHTML(touchpointData.finalHtml);

    if (touchpointData.data?.jsonOutput) {
      let jsonOutput = touchpointData.data.jsonOutput;

      if (touchpointData.aiGenerated) {
        const parsedJson = JSON.parse(jsonOutput) as BeeJsonOutput;
        const needsAnnotationIds = !parsedJson.page?.rows?.some((row) =>
          row.columns?.some((column) => column.modules?.some((module) => module.descriptor?.id)),
        );

        if (needsAnnotationIds) {
          const annotatedJson = addAnnotationIdToElements(jsonOutput);

          // The PATCH writes this version into the detail cache the caller reads, which re-runs it and
          // lands back here. Backfilling once per version keeps that from becoming a PATCH loop if a
          // response ever echoes back un-annotated `jsonOutput`.
          if (annotationBackfillVersionIdRef.current !== touchpointData.id) {
            annotationBackfillVersionIdRef.current = touchpointData.id;

            editVersionMutation
              .mutateAsync({
                id: touchpointData.id,
                attributes: {
                  data: {
                    ...touchpointData.data,
                    jsonOutput: annotatedJson,
                  },
                } as Record<string, unknown>,
              })
              .catch((err) => {
                console.error("Failed to save annotation IDs:", err);
              });
          }

          jsonOutput = annotatedJson;
        }
      }
      setTouchpointJSON(jsonOutput);
    }

    if (touchpointData.type === TouchpointType.EMAIL) {
      const email = touchpointData as EmailAttributes;
      const currentEmailType = emailTypeOptions?.find(
        (type) => type.id === email.iterableEmailTypeId,
      ) as IEmailType;

      const currentSender = getSenderOnEmailTypeChange(currentEmailType, email);

      const emailHeaders: EmailHeaders = {
        senderName: currentSender?.senderName || initialEmailHeaders.senderName,
        replyToEmail: currentSender?.replyToEmail || initialEmailHeaders.replyToEmail,
        subjectLine: email.subjectLine || initialEmailHeaders.subjectLine,
        preheader: email.preheader || initialEmailHeaders.preheader,
        senderEmail: currentSender?.senderEmail || initialEmailHeaders.senderEmail,
      };
      setEmailHeaders(emailHeaders);
    }
  }

  const createDraftSubmit = ({ description, applyClientTheme, fiscalYear }: SubmitFormProps) => {
    return createVersionMutation
      .mutateAsync({
        description,
        applyClientTheme: applyClientTheme!,
        fiscalYear,
      })
      .then(() => {
        toast.success({
          data: {
            title: "Created draft",
          },
        });
        invalidateTouchpointVersions();
      })
      .catch((err) => {
        toast.error({
          data: {
            title: "Failed to create draft",
            message: getErrorMessage(err?.response?.data),
          },
        });
        invalidateTouchpointVersions();
      });
  };

  const selectedVersionId = selectedVersion?.id;

  const {
    data: versionDetail,
    isError: isVersionDetailError,
    error: versionDetailError,
  } = useTouchpointVersion(
    {
      clientId: currentClient.id,
      campaignId: currentCampaign.id,
      touchpointId: currentTouchpoint.id,
      id: selectedVersionId,
    },
    { enabled: selectedVersion?.touchpointId === currentTouchpoint.id },
  );

  const wasEmailWriterGeneratingRef = useRef(false);

  useEffect(() => {
    if (selectedVersionId) {
      setIsLoading(true);
    }
  }, [selectedVersionId]);

  useEffect(() => {
    if (!versionDetail) {
      return;
    }

    setPreviewError(false);
    setTouchpointValues(versionDetail);
    setIsLoading(false);
    // `setTouchpointValues` is a function declaration in the component body, so it has a new identity on
    // every render. In the deps it would re-run this effect — and the annotation PATCH inside it — per render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [versionDetail, emailOptionsDependency]);

  useEffect(() => {
    if (!isVersionDetailError) {
      return;
    }

    setPreviewError(true);
    setIsLoading(false);
    console.error("Failed to load touchpoint version", versionDetailError);
    // Without this, `previewError` alone renders `NoVersionFound` — a load failure would read as
    // "you haven't built any versions yet" and offer to create a draft.
    toast.error({
      data: {
        title: "Failed to load version",
        message: getErrorMessage(versionDetailError),
      },
    });
  }, [isVersionDetailError, versionDetailError]);

  // Separately clear loading when there is no version and the parent has finished loading.
  useEffect(() => {
    if (!selectedVersionId && !isVersionHistoryLoading) {
      setIsLoading(false);
    }
  }, [selectedVersionId, isVersionHistoryLoading]);

  useEffect(() => {
    if (isEmailWriterGenerating) {
      wasEmailWriterGeneratingRef.current = true;
      return;
    }

    if (!wasEmailWriterGeneratingRef.current) {
      return;
    }

    // Rewrite archives the previous draft and creates a new one — select the newest
    // version so the picker/preview update while staying on this page.
    // Keep the "was generating" flag until success so Strict Mode remount can retry.
    let cancelled = false;

    const selectNewestVersionAfterRewrite = async () => {
      setIsLoading(true);

      try {
        const result = await refetchTouchpointVersions();
        const refreshedVersions = result.data?.items ?? [];

        if (cancelled) {
          return;
        }

        if (refreshedVersions.length === 0) {
          wasEmailWriterGeneratingRef.current = false;
          setIsLoading(false);
          return;
        }

        setSelectedVersion(refreshedVersions[0]);
        wasEmailWriterGeneratingRef.current = false;
      } catch (error) {
        console.error("Failed to select newest version after email rewrite:", error);
        if (!cancelled) {
          toast.error({
            data: {
              title: "Failed to load rewritten email",
              message:
                getErrorMessage(error) ||
                "Rewrite completed, but the newest version could not be selected. Please refresh and try again.",
            },
          });
          wasEmailWriterGeneratingRef.current = false;
          setIsLoading(false);
        }
      }
    };

    void selectNewestVersionAfterRewrite();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line
  }, [isEmailWriterGenerating]);

  if (isEmailWriterGenerating) {
    return <EmailWriterPreviewLoadingState />;
  }

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", mt: "2.5rem", justifyContent: "center" }}>
        <LoadingSpinner />
      </Box>
    );
  }

  if (!selectedVersion?.id || previewError) {
    return (
      <NoVersionFound touchpointType={currentTouchpoint.type} onCreateDraft={createDraftSubmit} />
    );
  }

  const dataTestId = isDesktopPreview
    ? "touchpoint-desktop-previewType"
    : "touchpoint-mobile-previewType";

  const builderRoute = usesLandingPageBuilder(currentTouchpoint)
    ? "landing-page-builder"
    : "email-builder";
  const designTabRedirect = location.pathname.replace(
    "preview",
    `versions/${selectedVersion?.id}/${builderRoute}/design`,
  );
  const customClass = isMobilePreview ? "max-w-[25rem]" : "max-w-full";

  if (touchpointPreviewHTML && touchpointFinalHTML) {
    return (
      <Stack
        data-testid="preview-tab-content"
        spacing={0}
        sx={{ alignItems: "center", flex: "1", mx: "auto", width: "100%" }}>
        <BuilderPreviewPaneHeader
          currentView={currentView}
          selectedVersion={selectedVersion}
          setCurrentView={setCurrentView}
          isCommentMode={isCommentMode}
          setIsCommentMode={setIsCommentMode}
          isCommentModeDisabled={false}
          isTouchpointPreview={true}
          isFullContentMode={isFullContentMode}
          setIsFullContentMode={setIsFullContentMode}
        />
        <div className={`w-full pt-2 ${customClass}`} data-testid={dataTestId}>
          {isDesignPreview ? (
            <>
              <DesignPreview
                emailHeaders={
                  currentTouchpoint.type === TouchpointType.EMAIL ? emailHeaders : undefined
                }
                hideSubjectAndPreheader={isTemplate}
                htmlContent={touchpointPreviewHTML}
                htmlJSON={touchpointJSON}
                isCommentMode={isCommentMode}
                isFullContentMode={isFullContentMode}
                mobileView={isMobilePreview}
              />
              {selectedVersion.aiGenerated &&
                isProgramBriefsEnabled &&
                !hasClientPortalAccess &&
                !aiContentFeedbackHiddenStatuses.includes(
                  selectedVersion.status as TouchpointStatuses,
                ) && (
                  <AiContentFeedback
                    clientId={currentClient.id}
                    campaignId={currentCampaign.id}
                    touchpointId={currentTouchpoint.id}
                    versionId={selectedVersion.id}
                    aiFeedbackRating={selectedVersion.aiFeedbackRating}
                    onFeedbackRecorded={(rating) =>
                      setSelectedVersion({
                        ...selectedVersion,
                        aiFeedbackRating: rating,
                      })
                    }
                  />
                )}
            </>
          ) : (
            <BuilderHtmlPreview
              data-testid="preview-html"
              textareaTestId="preview-textarea"
              value={touchpointFinalHTML}
            />
          )}
        </div>
      </Stack>
    );
  }

  return <EmptyDesignPreview redirectTo={designTabRedirect} currentVersion={selectedVersion} />;
};
